import { useEffect } from "react";
import { X, Sliders, RotateCw, Globe } from "lucide-react";
import { useAppMode } from "../hooks";

export interface CardTransform {
  rotateX: number;
  rotateY: number;
  rotateZ: number;
  perspective: number;
  scale: number;
  hideBottomControls: boolean;
}

export const DEFAULT_TRANSFORM: CardTransform = {
  rotateX: 0,
  rotateY: 0,
  rotateZ: 0,
  perspective: 1000,
  scale: 1,
  hideBottomControls: false,
};

interface TestControlDialogProps {
  isOpen: boolean;
  onClose: () => void;
  transform: CardTransform;
  onTransformChange: (transform: CardTransform) => void;
}

interface SliderControlProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  onChange: (value: number) => void;
}

function SliderControl({ label, value, min, max, step = 1, unit = "", onChange }: SliderControlProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-foreground">{label}</label>
        <span className="text-xs font-mono text-muted-foreground tabular-nums">
          {value}{unit}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="flex-1 h-2 rounded-full appearance-none bg-white/10 cursor-pointer
            [&::-webkit-slider-thumb]:appearance-none
            [&::-webkit-slider-thumb]:w-4
            [&::-webkit-slider-thumb]:h-4
            [&::-webkit-slider-thumb]:rounded-full
            [&::-webkit-slider-thumb]:bg-accent
            [&::-webkit-slider-thumb]:shadow-lg
            [&::-webkit-slider-thumb]:cursor-pointer
            [&::-webkit-slider-thumb]:transition-transform
            [&::-webkit-slider-thumb]:hover:scale-110
            [&::-moz-range-thumb]:w-4
            [&::-moz-range-thumb]:h-4
            [&::-moz-range-thumb]:rounded-full
            [&::-moz-range-thumb]:bg-accent
            [&::-moz-range-thumb]:border-0
            [&::-moz-range-thumb]:cursor-pointer"
        />
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className="w-20 px-2 py-1 text-xs font-mono text-center rounded-lg bg-white/5 border border-white/10 text-foreground focus:outline-none focus:border-accent/50"
        />
      </div>
    </div>
  );
}

export function TestControlDialog({ isOpen, onClose, transform, onTransformChange }: TestControlDialogProps) {
  const { mode, isGeneric, setModeOverride, modeOverride } = useAppMode();

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const handleReset = () => {
    onTransformChange(DEFAULT_TRANSFORM);
  };

  const updateTransform = (key: keyof CardTransform, value: number) => {
    onTransformChange({ ...transform, [key]: value });
  };

  const handleModeToggle = () => {
    // Toggle between generic and ai modes
    const newMode = isGeneric ? "ai" : "generic";
    setModeOverride(newMode);
  };

  const clearModeOverride = () => {
    setModeOverride(null);
  };

  // Copy current transform as CSS
  const copyTransformCSS = () => {
    const css = `perspective: ${transform.perspective}px;
transform: rotateX(${transform.rotateX}deg) rotateY(${transform.rotateY}deg) rotateZ(${transform.rotateZ}deg) scale(${transform.scale});
transform-style: preserve-3d;`;
    navigator.clipboard.writeText(css);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end p-4 pointer-events-none">
      {/* Dialog - positioned top-right */}
      <div className="relative w-full max-w-sm glass-strong rounded-2xl p-5 animate-in pointer-events-auto mt-16">
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 rounded-xl bg-purple-500/20 text-purple-400">
            <Sliders className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Test Controls</h2>
            <p className="text-xs text-muted-foreground">Card 3D Transform</p>
          </div>
        </div>

        {/* Transform Controls */}
        <div className="space-y-5">
          <SliderControl
            label="Rotate X"
            value={transform.rotateX}
            min={-90}
            max={90}
            unit="°"
            onChange={(v) => updateTransform("rotateX", v)}
          />
          
          <SliderControl
            label="Rotate Y"
            value={transform.rotateY}
            min={-90}
            max={90}
            unit="°"
            onChange={(v) => updateTransform("rotateY", v)}
          />
          
          <SliderControl
            label="Rotate Z"
            value={transform.rotateZ}
            min={-180}
            max={180}
            unit="°"
            onChange={(v) => updateTransform("rotateZ", v)}
          />
          
          <SliderControl
            label="Perspective"
            value={transform.perspective}
            min={100}
            max={3000}
            step={25}
            unit="px"
            onChange={(v) => updateTransform("perspective", v)}
          />
          
          <SliderControl
            label="Scale"
            value={transform.scale}
            min={0.25}
            max={2}
            step={0.05}
            onChange={(v) => updateTransform("scale", v)}
          />

          {/* Toggle Controls */}
          <div className="pt-2 border-t border-white/10 space-y-3">
            <label className="flex items-center justify-between cursor-pointer group">
              <span className="text-sm font-medium text-foreground">Hide bottom controls</span>
              <button
                type="button"
                role="switch"
                aria-checked={transform.hideBottomControls}
                onClick={() => updateTransform("hideBottomControls", transform.hideBottomControls ? 0 : 1)}
                className={`
                  relative w-11 h-6 rounded-full transition-colors duration-200
                  ${transform.hideBottomControls ? "bg-accent" : "bg-white/10"}
                `}
              >
                <span
                  className={`
                    absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow-md
                    transition-transform duration-200
                    ${transform.hideBottomControls ? "translate-x-5" : "translate-x-0"}
                  `}
                />
              </button>
            </label>
          </div>

          {/* App Mode Controls */}
          <div className="pt-4 border-t border-white/10 space-y-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Globe className="w-3.5 h-3.5" />
              <span>Domain Mode</span>
              {modeOverride && (
                <button
                  type="button"
                  onClick={clearModeOverride}
                  className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-white/10 hover:bg-white/20 transition-colors"
                >
                  Clear Override
                </button>
              )}
            </div>
            
            <label className="flex items-center justify-between cursor-pointer group">
              <div className="flex flex-col">
                <span className="text-sm font-medium text-foreground">
                  Generic Mode
                  {modeOverride && (
                    <span className="ml-1.5 text-[10px] text-yellow-400 font-normal">(overridden)</span>
                  )}
                </span>
                <span className="text-xs text-muted-foreground">
                  {isGeneric ? "pickthree.me" : "pickthree.ai"} experience
                </span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={isGeneric}
                onClick={handleModeToggle}
                className={`
                  relative w-11 h-6 rounded-full transition-colors duration-200
                  ${isGeneric ? "bg-green-500" : "bg-white/10"}
                `}
              >
                <span
                  className={`
                    absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow-md
                    transition-transform duration-200
                    ${isGeneric ? "translate-x-5" : "translate-x-0"}
                  `}
                />
              </button>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6 pt-4 border-t border-white/10">
          <button
            type="button"
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
          >
            <RotateCw className="w-4 h-4" />
            Reset
          </button>
          <button
            type="button"
            onClick={copyTransformCSS}
            className="flex-1 px-4 py-2 rounded-xl text-sm font-medium bg-accent/20 text-accent hover:bg-accent/30 transition-colors"
          >
            Copy CSS
          </button>
        </div>

        {/* Dev badge */}
        <div className="absolute -top-2 -left-2 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-yellow-500 text-black rounded-full">
          DEV
        </div>
      </div>
    </div>
  );
}

